import React, { Component } from 'react';
import { Drawer, Intent, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import colors from 'utils/colors';
import { DialogWrapper } from 'components/DialogWrapper';
import {
  DrawerHeader,
  FlexRow,
  BlueTextPointer,
  PlainButton,
} from 'components/styled-components';
import clone from 'lodash/clone';
import isEqual from 'lodash/isEqual';
import FormTextAreaGroup from 'components/forms/components/form-text-area-group';
import ErrorWrapper from 'components/forms/components/error-wrapper';
import {
  updateQuestionnaireAnswer,
  updateQuestionnaireDraftAnswer,
  archiveQuestionnaireAnswer,
} from 'api/questionnaire';
import { AppToaster } from 'components/AppToaster';
import { AnswerDetails } from 'components/forms/questionnaire-management/answer-details';
import {
  Remove,
  BreadCrumb,
  QuestionLabel,
  Question,
  ScrollWrapper,
} from 'containers/Manage-Questionnaire/styled-components';
import { statusByVersionStatus } from 'utils/functions';

class QuestionnaireEditAnswerDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editMade: false,
      answer: clone(this.props.answer),
      removeAnswer: false,
      closeWithoutSaving: false,
      answerError: false,
      scoreError: false,
      displayCategoryError: false,
    };
  }

  handleCloseDrawer = () => {
    if (this.state.editMade) {
      this.toggleCloseWithoutSaving();
    } else {
      this.setState({ answer: clone(this.props.answer) });
      this.props.closeDrawer();
    }
  };

  handlePageChange = nextPage => {
    this.props.closeDrawer();
    this.props.setQuestionnaireIndex(nextPage);
  };

  returnToParent = () => {
    if (this.props.isParentOpen) {
      this.handleCloseDrawer();
    } else {
      this.props.openParent();
      this.handleCloseDrawer();
    }
  };

  handleTextEdit = ({ target }) => {
    const { value } = target;
    const { answer } = this.state;
    answer.answer = value;
    this.setState({ answer });
    this.validateAnswerValue(value);
    this.toggleEditMade(answer);
  };

  validateAnswerValue = answerValue => {
    if (answerValue.length > 0) {
      this.setState({ answerError: false });
    } else {
      this.setState({ answerError: true });
    }
  };

  handleAutoFailChange = () => {
    const { answer } = this.state;
    answer.autoFail = !this.state.answer.autoFail;
    this.setState({ answer });
    this.toggleEditMade(answer);
  };

  handleScoreChange = score => {
    const { answer } = this.state;
    answer.score = score;
    this.setState({ answer });
    this.toggleEditMade(answer);
    this.validateScore(score);
  };

  validateScore = score =>
    this.setState({ scoreError: score <= 0 ? true : false });

  toggleEditMade = answer => {
    if (isEqual(answer, this.props.answer)) {
      this.setState({ editMade: false });
    } else {
      this.setState({ editMade: true });
    }
  };

  handleDismissRemoveAnswer = () => this.setState({ removeAnswer: false });
  openRemoveAnswerDialog = () => this.setState({ removeAnswer: true });
  toggleCloseWithoutSaving = () =>
    this.setState({ closeWithoutSaving: !this.state.closeWithoutSaving });

  handleArchiveAnswer = (id, status) => {
    archiveQuestionnaireAnswer(id, status)
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        this.props.closeDrawer();
        this.props.fetchQuestionnaireQuestions();
      })
      .catch(err => {
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  handleUpdateAnswer = () => {
    const { answer } = this.state;
    if (answer.versionStatus === 'Active') {
      updateQuestionnaireAnswer(answer, this.props.question.questionID)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.props.fetchQuestionnaireQuestions();
          this.setState({ editMade: false });
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    } else {
      updateQuestionnaireDraftAnswer(answer)
        .then(res => {
          AppToaster.show({
            message: res.data.message,
            intent: Intent.SUCCESS,
          });
          this.props.fetchQuestionnaireQuestions();
          this.setState({ editMade: false });
        })
        .catch(err => {
          AppToaster.show({
            message: err.response.data.message,
            intent: Intent.DANGER,
          });
        });
    }
  };

  handleUpdateLocalAnswer = () => {
    const { answer } = this.state;
    this.props.handleAnswerValueChange(answer, this.props.index);
    this.setState({ editMade: false });
    this.props.closeDrawer();
  };

  render() {
    const {
      answer,
      editMade,
      answerError,
      scoreError,
      displayCategoryError,
    } = this.state;
    const { index, question, currentIndex, isOpen } = this.props;

    const status = statusByVersionStatus(answer.versionStatus);

    return (
      <Drawer
        isOpen={isOpen || currentIndex === index}
        onClose={this.handleCloseDrawer}
        hasBackdrop={!this.props.isParentOpen}
      >
        <DrawerHeader>
          Edit Answer
          {editMade ? (
            <FlexRow fontSize="0.7rem" width="15rem">
              <BlueTextPointer
                onClick={() => this.toggleExitWithoutSaving(true)}
              >
                DISCARD
              </BlueTextPointer>
              <PlainButton
                onClick={
                  this.props.newQuestion
                    ? this.handleUpdateLocalAnswer
                    : this.handleUpdateAnswer
                }
                disabled={answerError || displayCategoryError}
              >
                SAVE CHANGES
              </PlainButton>
            </FlexRow>
          ) : (
            <Icon
              icon={IconNames.CROSS}
              iconSize={Icon.SIZE_LARGE}
              onClick={this.handleCloseDrawer}
              style={{ cursor: 'pointer' }}
            />
          )}
        </DrawerHeader>
        <BreadCrumb onClick={this.returnToParent}>
          <Icon icon={IconNames.CHEVRON_LEFT} iconSize={25} />
          <span>Edit Question</span>
        </BreadCrumb>
        <ScrollWrapper>
          <QuestionLabel>Question</QuestionLabel>
          <Question>{question.question}</Question>
          {answerError && (
            <ErrorWrapper>The answer field cannot be blank.</ErrorWrapper>
          )}
          <FormTextAreaGroup
            label="Answer"
            requiredLabel={false}
            name="answer"
            fill={true}
            value={answer.answer}
            handleChange={this.handleTextEdit}
            style={{ background: colors.formBackground }}
          />
          <AnswerDetails
            score={answer.score}
            scoreError={scoreError}
            autoFail={answer.autoFail}
            handleAutoFailChange={this.handleAutoFailChange}
            handleScoreChange={this.handleScoreChange}
          />

          <Remove onClick={this.openRemoveAnswerDialog}>
            {'Remove Answer'}
          </Remove>

          <DialogWrapper
            isOpen={this.state.removeAnswer}
            onClose={this.handleDismissRemoveAnswer}
            title={'Remove Answer'}
            body={status.getBodyAnswer}
            confirm={'REMOVE'}
            action={
              this.props.newQuestion
                ? () => {
                    this.props.archiveLocalAnswer(index);
                    this.props.closeDrawer();
                  }
                : () =>
                    this.handleArchiveAnswer(
                      answer.answerID,
                      answer.versionStatus
                    )
            }
          />

          <DialogWrapper
            isOpen={this.state.closeWithoutSaving}
            onClose={this.toggleCloseWithoutSaving}
            title="You have unsaved changes on this page"
            body="If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page?"
            confirm="DISCARD CHANGES"
            action={() => {
              this.setState(
                { editMade: false, closeWithoutSaving: false },
                () => this.handleCloseDrawer()
              );
            }}
          />
        </ScrollWrapper>
      </Drawer>
    );
  }
}

export default QuestionnaireEditAnswerDrawer;
